import type { UserRole } from '../types'

/**
 * Check if a user role has write permissions (can create, edit, delete resources).
 * Viewer role is read-only and cannot write.
 */
export function canWrite(role: UserRole | undefined): boolean {
  if (!role) return false
  return role !== 'Viewer'
}

/**
 * Check if a user role is Admin.
 */
export function isAdmin(role: UserRole | undefined): boolean {
  return role === 'Admin'
}

/**
 * Check if a user role can ingest events.
 */
export function canIngest(role: UserRole | undefined): boolean {
  if (!role) return false
  return role === 'UserWithIngest' || role === 'ProjectOwner' || role === 'Admin'
}

/**
 * Get a user-friendly error message based on the error and user role.
 * If the user is a Viewer and gets a 403, return a friendly message.
 * Otherwise, return the original error message.
 */
export function getErrorMessage(
  error: unknown,
  role: UserRole | undefined,
  viewerMessage: string,
  fallbackMessage: string
): string {
  // Check if it's a 403 error
  const is403 = error instanceof Error && (
    error.message.includes('403') ||
    (error as { response?: { status?: number } }).response?.status === 403
  )

  // If it's a 403 and user is Viewer, return the friendly message
  if (is403 && role === 'Viewer') {
    return viewerMessage
  }

  // Otherwise return the error message or fallback
  return error instanceof Error ? error.message : fallbackMessage
}
